import React from "react";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import scrollHandler from "../../utils/scrollHandler";
import { useNavigate, Link } from "react-router-dom";
import pathnames from "../../constants/pathnames";
import AppTextField from "../../lib/components/AppTextField";
import { Button, InputAdornment } from "@mui/material";
import { Search } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import referralsService from "../../service/referralsService";
import walletService from "../../service/walletService";
import PayoutsDataTable from "./components/payouts-data-table";
import useDebounce from "../../hooks/useDebounce";
import { toast } from "react-toastify";
import AppExportButton from "../../lib/components/AppExportButton";
import { moneyFormat } from "../../utils/moneyFormat";
import MoneyIcon from "../../lib/assets/icons/referral__icon-2.svg";
import MoneyIconBlue from "../../lib/assets/icons/referral__icon-5.svg";
import MoneyCashIcon from "../../lib/assets/icons/referral__icon-1.svg";
import FundWalletModal from "./components/fund-wallet-modal";
import StatsCard from "./components/stats-card";

const Payouts = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get("page") || "1";
  const limit = searchParams.get("limit") || "20";
  const keyword = searchParams.get("keyword") || "";

  const searchTerm = useDebounce(keyword);
  const query = { page, limit, keyword: searchTerm };

  const payoutsQuery = useQuery({
    retry: false,
    queryKey: ["paidReferrers", ...Object.values(query)],
    queryFn: () => referralsService.getPaidReferrers(query),
  });

  const paystackBalanceQuery = useQuery({
    retry: false,
    queryKey: ["paystackBalance"],
    queryFn: () => walletService.getPaystackBalance(),
  });

  const payoutsData = payoutsQuery?.data?.data;
  const paystackBalanceData = paystackBalanceQuery?.data?.data?.data || paystackBalanceQuery?.data?.data;
  const paystackBalance = paystackBalanceData?.balance ?? 0;

  const [isExporting, setIsExporting] = React.useState(false);
  const [fundWalletOpen, setFundWalletOpen] = React.useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await referralsService.exportPaidReferrers(searchTerm);
      toast.success("CSV exported successfully!");
    } catch (e) {
      toast.error("Failed to export Paid Referrers CSV");
    } finally {
      setIsExporting(false);
    }
  };

  const handleFundWallet = () => {
    setFundWalletOpen(true);
  };

  const summary = payoutsData?.summary || payoutsData?.data?.summary;
  const totalCommission = summary?.totalCommission ?? summary?.totalEarned ?? 0;
  const totalPaid = summary?.totalPaid ?? summary?.totalPayouts ?? summary?.successfulAmount ?? 0;
  const completedTransactions = summary?.completedTransactions ?? summary?.successful ?? 0;

  return (
    <AppLayout>
      <Header
        title="Payout"
        description="Get insights to Referrers that have been paid from this page"
      />
      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <div className="row">
          <StatsCard
            title="Total Commission Earned"
            amount={moneyFormat(totalCommission, "NGN")}
            icon={MoneyIcon}
            isLoading={payoutsQuery.isLoading}
          />
          <StatsCard
            title="Total Payouts"
            amount={moneyFormat(totalPaid, "NGN")}
            subtitle={`${completedTransactions} Completed Transactions`}
            icon={MoneyCashIcon}
            isLoading={payoutsQuery.isLoading}
          />
          <StatsCard
            title="Wallet Balance"
            amount={moneyFormat(paystackBalance, "NGN")}
            icon={MoneyIconBlue}
            isLoading={paystackBalanceQuery.isLoading}
          />
        </div>

        <div className="row mb-3 align-items-center">
          <div className="col-12 col-md-4 mb-3">
            <AppTextField
              placeholder="Search referrers..."
              value={keyword}
              onChange={(e) => handleChange(e, "keyword")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search className="text-secondary" />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div className="col-12 col-md-8 mb-3 d-flex justify-content-md-end gap-3 align-items-center flex-wrap">
            <Button component={Link} to={pathnames.PAYOUTS__FUNDING_HISTORY} variant="text" color="secondary">
              View Funding History
            </Button>
            <AppExportButton onClick={handleExport} isLoading={isExporting} />
            <Button variant="contained" color="secondary" onClick={handleFundWallet}>
              Fund Wallet
            </Button>
          </div>
        </div>

        <PayoutsDataTable
          isLoading={payoutsQuery.isLoading}
          data={payoutsData?.docs || payoutsData?.data}
          page={Number(page)}
          pageSize={Number(limit)}
          totalPages={payoutsData?.totalPages}
          onPageChange={(value) => handleChange(value, "page")}
          onPageSizeChange={(value) =>
            handleChange({ target: { value } }, "limit")
          }
        />
      </div>
      <FundWalletModal
        open={fundWalletOpen}
        onClose={() => setFundWalletOpen(false)}
        refetch={paystackBalanceQuery.refetch}
      />
    </AppLayout>
  );

  function handleChange(e, name = "") {
    const value = name === "page" ? e : e.target.value;
    searchParams.set(name, value);
    if (name === "keyword") {
      searchParams.set("page", "1");
    }
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
      { scrollY: 0 }
    );
    if (name === "page") scrollHandler();
  }
};

export default Payouts;
